import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid';
import { DataGridProps } from 'models/dataGridCustom.model';
import SkeletonTable from '../SkeletonTable';
import PaginationCustom from './PaginationCustom';
import { Tooltip, Typography } from '@mui/material';
import { useMemo, RefAttributes } from 'react';

const DataGridCustom = <T extends object>({
  isPaginated,
  columns,
  data,
  isLoading,
  state,
  sx,
  getRowId,
  ...otherProps
}: DataGridProps<T> & RefAttributes<HTMLDivElement>) => {
  const apiRef = useGridApiRef();
  const columnsDefinition: GridColDef[] = useMemo(
    () =>
      columns.map<GridColDef>((column) => ({
        renderCell: (
          params: GridRenderCellParams<GridValidRowModel, T, GridTreeNodeWithRender>,
        ) => (
          <Tooltip title={params.value?.toString() ?? ''} arrow>
            <Typography sx={{ maxWidth: '100%' }}>{params.value?.toString() ?? ''}</Typography>
          </Tooltip>
        ),
        ...column,
      })),
    [columns],
  );

  return (
    <DataGrid
      apiRef={apiRef}
      disableVirtualization
      autoPageSize={isPaginated}
      columns={columnsDefinition}
      rows={(isPaginated ? data?.content : data) ?? []}
      rowCount={(isPaginated ? data?.totalElements : data?.length) ?? 0}
      loading={isLoading}
      slots={{
        loadingOverlay: SkeletonTable,
        pagination: PaginationCustom,
      }}
      slotProps={{
        loadingOverlay: {
          columnsSize: columnsDefinition.map((column) => ({
            flex: column.flex ?? 0,
            width: column.width ?? 0,
          })),
          rowsCount: state.pageSize,
        },
        pagination: {
          rowCount: isPaginated ? data?.totalElements ?? 0 : undefined,
        },
      }}
      disableColumnMenu={true}
      rowSelection={false}
      getRowId={getRowId}
      getRowClassName={() => 'custom-data-grid-row'}
      paginationModel={
        // The DataGrid (MIT license) is limited to pages of up to 100 rows.
        isPaginated ? { page: state.page, pageSize: state.pageSize } : { page: 0, pageSize: 100 }
      }
      paginationMode={isPaginated ? 'server' : 'client'}
      sx={{
        'backgroundColor': 'transparent',
        'borderRadius': '8px',
        'margin': 'auto',
        'width': '100%',
        'minWidth': 0,
        'color': 'darkText.main',
        'border': 'none',
        '& .MuiDataGrid-columnSeparator': {
          display: 'none',
        },
        '& *': {
          fontFamily: "'Nunito Sans Variable', sans-serif",
        },
        '& .MuiDataGrid-virtualScroller': {
          overflow: 'hidden',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 700,
        },
        '& .custom-data-grid-row': {
          backgroundColor: 'background.default',
          borderBottom: 'none',
        },
        '& .custom-data-grid-row:hover': {
          backgroundColor: 'background.paper',
        },
        '& .MuiDataGrid-footerContainer': {
          justifyContent: 'space-evenly',
        },
        '& .MuiDataGrid-footerContainer > div': {
          display: 'none',
        },
        '& button': {
          borderRadius: '8px',
          backgroundColor: 'transparent',
        },
        '& button.MuiPaginationItem-previousNext ': {
          color: 'primary.main',
        },
        '& button:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.12)',
        },
        '& button.Mui-selected': {
          backgroundColor: 'transparent',
          color: 'primary.main',
          border: '1px solid',
          borderColor: 'primary.main',
        },
        ...sx,
      }}
      {...otherProps}
    />
  );
};

export default DataGridCustom;
